import React, { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  Users,
  GraduationCap,
  Briefcase,
  Building2,
  RefreshCw,
  Cpu,
  Link,
  Check,
  X,
  CheckCircle2,
  Calendar,
  ArrowRight,
  ArrowUpRight,
  Mail,
  Linkedin,
  Globe,
  Newspaper
} from "lucide-react";
import {
  companyInfo,
  marketData,
  competitiveData,
  b2bCompetitiveData,
  moatData,
  businessModel,
  roadmapData,
  teamMembers,
  investmentAsk
} from "../data/mock";
import { useDarkMode } from "./InvestorDeckV3";

// CASEY Logo URLs - SVG logos
const CASEY_LOGOS = {
  dark: "https://customer-assets.emergentagent.com/job_investor-casey/artifacts/fcxzuz8r_5.svg", // For light/white backgrounds (dark logo)
  light: "https://customer-assets.emergentagent.com/job_investor-casey/artifacts/k0cvyyz4_6.svg", // For dark backgrounds (light/white logo)
};

// Animated Number
const MorphNumber = ({ value, suffix = "", prefix = "" }) => {
  const [count, setCount] = React.useState(0);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });
  const numericValue = parseInt(value.toString().replace(/[^0-9]/g, ""));

  React.useEffect(() => {
    if (isInView && numericValue) {
      const duration = 2000;
      const steps = 60;
      const increment = numericValue / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= numericValue) {
          setCount(numericValue);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, numericValue]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Reveal Text
const RevealText = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px" });

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: "100%" }}
        animate={isInView ? { y: 0 } : {}}
        transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
};

// Market Section
export const MarketSectionV3 = () => {
  const { isDark } = useDarkMode();
  const iconMap = {
    "graduation-cap": GraduationCap,
    building: Building2,
    briefcase: Briefcase
  };

  const total = marketData.segments.reduce((acc, seg) => acc + seg.value, 0);

  return (
    <section id="market" className={`py-32 overflow-hidden transition-colors duration-500 ${isDark ? "bg-slate-900" : "bg-slate-50"}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${
              isDark ? "bg-casey-purple/20 text-casey-light-blue" : "bg-casey-purple/10 text-casey-purple"
            }`}
          >
            US Market Opportunity
          </motion.span>
          <RevealText>
            <h2 className={`font-montserrat text-5xl md:text-6xl mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>
              A $427B+ US Market
            </h2>
          </RevealText>
          <RevealText delay={0.2}>
            <h2 className="font-montserrat text-5xl md:text-6xl bg-gradient-to-r from-casey-blue to-casey-purple bg-clip-text text-transparent">
              Waiting for Clarity
            </h2>
          </RevealText>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Animated Donut Chart */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: "spring" }}
            className="relative"
          >
            <div className="aspect-square max-w-md mx-auto relative">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                <defs>
                  <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#011bc4" />
                    <stop offset="100%" stopColor="#2c46f4" />
                  </linearGradient>
                  <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#765ccb" />
                    <stop offset="100%" stopColor="#7b8cff" />
                  </linearGradient>
                </defs>
                {marketData.segments.map((segment, index) => {
                  const prevTotal = marketData.segments
                    .slice(0, index)
                    .reduce((acc, s) => acc + s.value, 0);
                  const percentage = segment.value / total;
                  const circumference = 2 * Math.PI * 70;
                  const offset = (prevTotal / total) * circumference;

                  return (
                    <motion.circle
                      key={index}
                      cx="100"
                      cy="100"
                      r="70"
                      fill="none"
                      stroke={index % 2 === 0 ? "url(#blueGradient)" : "url(#purpleGradient)"}
                      strokeWidth="25"
                      strokeDasharray={circumference}
                      strokeDashoffset={circumference * (1 - percentage)}
                      initial={{ strokeDashoffset: circumference }}
                      whileInView={{ strokeDashoffset: circumference * (1 - percentage) }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.5, delay: index * 0.2, ease: "easeOut" }}
                      style={{
                        transformOrigin: "center",
                        transform: `rotate(${(prevTotal / total) * 360 - 90}deg)`
                      }}
                      className="drop-shadow-lg"
                    />
                  );
                })}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className={`text-5xl font-montserrat font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  $<MorphNumber value="427" />B+
                </div>
                <div className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>US Market Size</div>
              </div>
            </div>
          </motion.div>

          {/* Market Segments */}
          <div className="space-y-8">
            {marketData.segments.map((segment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
                whileHover={{ x: 10, transition: { type: "spring", stiffness: 300 } }}
                className="group cursor-default"
              >
                <div className="flex items-start gap-4">
                  <motion.div
                    whileHover={{ scale: 1.3 }}
                    className="w-3 h-3 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: index % 2 === 0 ? "#011bc4" : "#765ccb" }}
                  />
                  <div>
                    <div className="flex items-baseline gap-3 mb-1">
                      <span className={`text-3xl font-montserrat font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                        ${segment.value}B
                      </span>
                      <span className={`font-semibold transition-colors ${
                        isDark ? "text-slate-300 group-hover:text-casey-light-blue" : "text-slate-700 group-hover:text-casey-blue"
                      }`}>
                        {segment.name}
                      </span>
                    </div>
                    <p className={`text-sm ${isDark ? "text-slate-500" : "text-slate-500"}`}>{segment.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Target Audience */}
        <div className="grid md:grid-cols-3 gap-6">
          {marketData.targetAudience.map((audience, index) => {
            const Icon = iconMap[audience.icon];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="relative group"
              >
                <div 
                  className={`absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity blur-xl ${
                    isDark ? "bg-casey-blue/30" : "bg-casey-blue/20"
                  }`}
                />
                <div 
                  className={`relative rounded-3xl p-10 text-center border ${
                    isDark 
                      ? "bg-slate-800 border-slate-700" 
                      : "bg-white border-slate-200 shadow-lg"
                  }`}
                >
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
                      isDark ? "bg-casey-blue/20" : "bg-casey-blue/10"
                    }`}
                  >
                    <Icon className={`w-10 h-10 ${isDark ? "text-casey-light-blue" : "text-casey-blue"}`} />
                  </motion.div>
                  <div className={`text-5xl font-montserrat font-bold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
                    {audience.value}
                  </div>
                  <div className={isDark ? "text-slate-400" : "text-slate-600"}>{audience.label}</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Competitive Section
export const CompetitiveSectionV3 = () => {
  const { isDark } = useDarkMode();
  const iconMap = {
    "refresh-cw": RefreshCw,
    cpu: Cpu,
    users: Users,
    link: Link
  };

  return (
    <section className={`py-24 md:py-32 overflow-hidden transition-colors duration-500 ${isDark ? "bg-slate-950" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium font-inter mb-6 ${
              isDark ? "bg-casey-blue/20 text-casey-light-blue" : "bg-casey-blue/10 text-casey-blue"
            }`}
          >
            Competitive Landscape
          </motion.span>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={`font-montserrat font-bold text-4xl md:text-5xl lg:text-6xl ${isDark ? "text-white" : "text-slate-900"}`}>
              CASEY's Unique
            </h2>
            <h2 
              className="font-montserrat font-bold text-4xl md:text-5xl lg:text-6xl"
              style={{
                background: "linear-gradient(to right, #011bc4, #765ccb)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}
            >
              Position
            </h2>
          </motion.div>
        </div>

        {/* B2C Competition Header */}
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`text-2xl font-montserrat font-semibold mb-6 ${isDark ? "text-white" : "text-slate-900"}`}
        >
          B2C Competition
        </motion.h3>

        {/* B2C Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`overflow-x-auto mb-8 rounded-2xl md:rounded-3xl p-4 md:p-8 ${isDark ? "bg-slate-900" : "bg-white border border-slate-200 shadow-lg shadow-slate-200/50"}`}
        >
          <table className="w-full min-w-[600px]">
            <thead>
              <tr>
                <th className={`text-left p-4 font-medium ${isDark ? "text-slate-400" : "text-slate-500"}`}>Feature</th>
                {competitiveData.competitors.map((comp) => (
                  <th
                    key={comp.name}
                    className={`p-4 text-center font-semibold ${
                      comp.highlight ? "text-casey-blue" : isDark ? "text-slate-300" : "text-slate-700"
                    }`}
                  >
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                      comp.highlight ? "bg-casey-blue text-white" : isDark ? "bg-slate-800" : "bg-slate-100"
                    }`}>
                      {comp.name}
                      {comp.highlight && (
                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                          Us
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {competitiveData.features.map((feature, i) => (
                <motion.tr
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`border-t ${isDark ? "border-slate-800" : "border-slate-200"}`}
                >
                  <td className={`p-4 font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>{feature}</td>
                  {competitiveData.competitors.map((comp) => (
                    <td key={comp.name} className="p-4 text-center">
                      {comp.scores[i] === "yes" ? (
                        <motion.div
                          whileHover={{ scale: 1.2, rotate: 10 }}
                          className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${isDark ? "bg-emerald-500/20" : "bg-emerald-100"}`}
                        >
                          <Check className={`w-5 h-5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                        </motion.div>
                      ) : comp.scores[i] === "partial" ? (
                        <motion.div
                          whileHover={{ scale: 1.2 }}
                          className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${isDark ? "bg-amber-500/20" : "bg-amber-100"}`}
                        >
                          <div className={`w-3 h-3 rounded-full ${isDark ? "bg-amber-400" : "bg-amber-500"}`} />
                        </motion.div>
                      ) : (
                        <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${isDark ? "bg-red-500/20" : "bg-red-100"}`}>
                          <X className={`w-5 h-5 ${isDark ? "text-red-400" : "text-red-500"}`} />
                        </div>
                      )}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* B2C Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`mb-16 md:mb-20 text-center max-w-3xl mx-auto ${isDark ? "text-slate-400" : "text-slate-600"}`}
        >
          <p className="mb-2">CASEY competes with career tools and coaching platforms focused on individuals.</p>
          <p>Unlike static job platforms or generic assessments, CASEY builds a living profile that evolves over time and delivers true career clarity.</p>
        </motion.div>

        {/* B2B Competition Header */}
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`text-2xl font-montserrat font-semibold mb-6 ${isDark ? "text-white" : "text-slate-900"}`}
        >
          B2B Competition
        </motion.h3>

        {/* B2B Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`overflow-x-auto mb-8 rounded-2xl md:rounded-3xl p-4 md:p-8 ${isDark ? "bg-slate-900" : "bg-white border border-slate-200 shadow-lg shadow-slate-200/50"}`}
        >
          <table className="w-full min-w-[800px]">
            <thead>
              <tr>
                <th className={`text-left p-4 font-medium ${isDark ? "text-slate-400" : "text-slate-500"}`}>Feature</th>
                {b2bCompetitiveData.competitors.map((comp) => (
                  <th
                    key={comp.name}
                    className={`p-4 text-center font-semibold ${
                      comp.highlight ? "text-casey-blue" : isDark ? "text-slate-300" : "text-slate-700"
                    }`}
                  >
                    <div className={`inline-flex flex-col items-center gap-1 px-3 py-2 rounded-xl ${
                      comp.highlight ? "bg-casey-blue text-white" : isDark ? "bg-slate-800" : "bg-slate-100"
                    }`}>
                      <span className="flex items-center gap-1">
                        {comp.name}
                        {comp.label && (
                          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                            {comp.label}
                          </span>
                        )}
                      </span>
                      {comp.subtitle && (
                        <span className={`text-xs ${comp.highlight ? "text-white/70" : isDark ? "text-slate-500" : "text-slate-500"}`}>
                          {comp.subtitle}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b2bCompetitiveData.features.map((feature, i) => (
                <motion.tr
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`border-t ${isDark ? "border-slate-800" : "border-slate-200"}`}
                >
                  <td className={`p-4 font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>{feature}</td>
                  {b2bCompetitiveData.competitors.map((comp) => (
                    <td key={comp.name} className="p-4 text-center">
                      {comp.scores[i] === "yes" ? (
                        <motion.div
                          whileHover={{ scale: 1.2, rotate: 10 }}
                          className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${isDark ? "bg-emerald-500/20" : "bg-emerald-100"}`}
                        >
                          <Check className={`w-5 h-5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                        </motion.div>
                      ) : comp.scores[i] === "partial" ? (
                        <motion.div
                          whileHover={{ scale: 1.2 }}
                          className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${isDark ? "bg-amber-500/20" : "bg-amber-100"}`}
                        >
                          <div className={`w-3 h-3 rounded-full ${isDark ? "bg-amber-400" : "bg-amber-500"}`} />
                        </motion.div>
                      ) : (
                        <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${isDark ? "bg-red-500/20" : "bg-red-100"}`}>
                          <X className={`w-5 h-5 ${isDark ? "text-red-400" : "text-red-500"}`} />
                        </div>
                      )}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* B2B Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`mb-16 md:mb-20 text-center max-w-3xl mx-auto ${isDark ? "text-slate-400" : "text-slate-600"}`}
        >
          <p className="mb-2">Most hiring systems manage resumes, workflows, or point-in-time assessments.</p>
          <p>CASEY provides longitudinal people intelligence that helps companies understand how candidates think, what motivates them, and where they will succeed.</p>
        </motion.div>

        {/* Moat */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-slate-900 rounded-3xl p-12 overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-casey-blue/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-casey-purple/20 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10">
            <h3 className="text-3xl font-montserrat font-semibold text-white text-center mb-12">
              Data is Our <span className="text-casey-light-blue">Moat</span>
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {moatData.map((item, index) => {
                const Icon = iconMap[item.icon];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-casey-blue/20 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-casey-light-blue" />
                    </div>
                    <h4 className="text-lg font-montserrat font-semibold text-white mb-2">
                      {item.title}
                    </h4>
                    <p className="text-sm text-white/50">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Business Model Section
export const BusinessModelSectionV3 = () => {
  const { isDark } = useDarkMode();
  
  return (
    <section className={`py-32 overflow-hidden transition-colors duration-500 ${isDark ? "bg-slate-900" : "bg-slate-50"}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${
              isDark ? "bg-casey-blue/20 text-casey-light-blue" : "bg-casey-blue/10 text-casey-blue"
            }`}
          >
            Business Model
          </motion.span>
          <div>
            <h2 
              className={`font-montserrat text-5xl md:text-6xl ${isDark ? "text-white" : "text-slate-900"}`}
            >
              Three Reinforcing
            </h2>
            <h2 
              className={`font-montserrat text-5xl md:text-6xl ${isDark ? "text-casey-light-blue" : "text-casey-blue"}`}
            >
              Revenue Streams
            </h2>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Revenue Breakdown */}
          <div className="space-y-10">
            {businessModel.channels.map((channel, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`font-montserrat text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {channel.name}
                  </span>
                  <span 
                    className="text-3xl font-montserrat font-bold" 
                    style={{ color: isDark 
                      ? (index === 0 ? "#7b8cff" : index === 1 ? "#a78bfa" : "#c4b5fd")
                      : (index === 0 ? "#011bc4" : index === 1 ? "#765ccb" : "#7b8cff") 
                    }}
                  >
                    {channel.percentage}%
                  </span>
                </div>
                <div className={`h-3 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${channel.percentage}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, delay: index * 0.15, ease: "easeOut" }}
                    className="h-full rounded-full"
                    style={{ background: isDark
                      ? (index === 0 ? "linear-gradient(90deg, #7b8cff, #93c5fd)" : index === 1 ? "linear-gradient(90deg, #a78bfa, #c4b5fd)" : "#c4b5fd")
                      : (index === 0 ? "linear-gradient(90deg, #011bc4, #2c46f4)" : index === 1 ? "linear-gradient(90deg, #765ccb, #7b8cff)" : "#7b8cff")
                    }}
                  />
                </div>
                <p className={`text-sm mt-3 ${isDark ? "text-slate-500" : "text-slate-500"}`}>{channel.description}</p>
              </motion.div>
            ))}
          </div>

          {/* ARR Target */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div 
              className={`absolute inset-0 rounded-3xl blur-2xl opacity-20 ${
                isDark ? "bg-casey-light-blue" : "bg-casey-blue"
              }`}
            />
            <div 
              className={`relative rounded-3xl p-8 md:p-10 text-center overflow-hidden border ${
                isDark 
                  ? "bg-slate-800 border-slate-700" 
                  : "bg-white border-slate-200 shadow-lg"
              }`}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className={`absolute -top-1/2 -right-1/2 w-full h-full border rounded-full ${
                  isDark ? "border-casey-light-blue/20" : "border-casey-blue/20"
                }`}
              />
              <div className="relative z-10">
                <div className={`text-5xl md:text-6xl font-montserrat font-bold mb-3 ${isDark ? "text-white" : "text-casey-blue"}`}>
                  $<MorphNumber value="100" />M
                </div>
                <div className={`text-xl mb-6 ${isDark ? "text-slate-400" : "text-slate-600"}`}>ARR Target</div>
                <div className={`inline-flex items-center gap-3 px-5 py-2.5 rounded-full text-sm ${
                  isDark ? "bg-casey-blue/20 text-casey-light-blue" : "bg-casey-blue/10 text-casey-blue"
                }`}>
                  <Calendar className="w-4 h-4" />
                  <span>By 2028</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Roadmap Section
export const RoadmapSectionV3 = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const lineHeight = useTransform(scrollYProgress, [0, 0.8], ["0%", "100%"]);

  return (
    <section ref={containerRef} className="py-32 bg-casey-blue overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-white/10 text-white/80"
          >
            Roadmap
          </motion.span>
          <RevealText>
            <h2 className="font-montserrat text-5xl md:text-6xl text-white">
              Our Road to
            </h2>
          </RevealText>
          <RevealText delay={0.2}>
            <h2 className="font-montserrat text-5xl md:text-6xl text-casey-light-blue">
              $100M ARR
            </h2>
          </RevealText>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Animated Line */}
          <div className="absolute left-8 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-0.5 bg-white/10">
            <motion.div
              style={{ height: lineHeight }}
              className="w-full bg-gradient-to-b from-casey-light-blue to-casey-purple"
            />
          </div>

          <div className="space-y-16">
            {roadmapData.map((item, index) => {
              const isLeft = index % 2 === 0;
              const statusColors = {
                current: "bg-white text-casey-blue",
                upcoming: "bg-casey-light-blue/30 text-white",
                future: "bg-white/10 text-white/70"
              };

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center gap-8 ${
                    isLeft ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  <div className={`flex-1 pl-20 md:pl-0 ${isLeft ? "md:text-right md:pr-16" : "md:pl-16"}`}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className="inline-block bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all"
                    >
                      <div className={`flex items-center gap-3 mb-4 ${isLeft ? "md:justify-end" : ""}`}>
                        <span className="text-sm text-white/50">
                          {item.year} {item.quarter}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[item.status]}`}>
                          {item.status === "current" ? "Now" : item.status}
                        </span>
                      </div>
                      <h3 className="text-2xl font-montserrat font-semibold text-white mb-3">
                        {item.title}
                      </h3>
                      <p className="text-white/50 text-sm mb-4">{item.description}</p>
                      <div className={`flex flex-wrap gap-2 ${isLeft ? "md:justify-end" : ""}`}>
                        {item.milestones.map((milestone, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/70"
                          >
                            {milestone}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  </div>

                  {/* Dot */}
                  <motion.div
                    whileHover={{ scale: 1.5 }}
                    className={`absolute left-8 md:left-1/2 md:-translate-x-1/2 w-4 h-4 rounded-full ${
                      item.status === "current" ? "bg-white" : "bg-casey-light-blue"
                    } ring-4 ring-casey-blue`}
                  />

                  <div className="hidden md:block flex-1" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Team Section
export const TeamSectionV3 = () => {
  const { isDark } = useDarkMode();
  
  return (
    <section id="team" className={`py-32 overflow-hidden transition-colors duration-500 ${isDark ? "bg-slate-950" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${
              isDark ? "bg-casey-blue/20 text-casey-light-blue" : "bg-casey-blue/10 text-casey-blue"
            }`}
          >
            The Team
          </motion.span>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={`font-montserrat text-5xl md:text-6xl ${isDark ? "text-white" : "text-slate-900"}`}>
              Led by Experienced
            </h2>
            <h2 
              className="font-montserrat text-5xl md:text-6xl"
              style={{
                background: "linear-gradient(to right, #011bc4, #765ccb)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}
            >
              Founders
            </h2>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="group"
            >
              <div className="relative overflow-hidden rounded-3xl mb-6">
                <motion.img
                  src={member.image}
                  alt={member.name}
                  className="w-full aspect-square object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-casey-blue/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                  {member.linkedin && (
                    <motion.a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ y: 20, opacity: 0 }}
                      whileHover={{ scale: 1.1 }}
                      className="bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                    </motion.a>
                  )}
                </div>
              </div>
              <div className="px-2">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full ${
                    member.type === "founder"
                      ? "bg-casey-blue text-white"
                      : member.type === "team"
                      ? "bg-casey-purple text-white"
                      : isDark ? "bg-slate-800 text-slate-300" : "bg-slate-200 text-slate-700"
                  }`}>
                    {member.type === "founder" ? "Founder" : member.type === "team" ? "Leadership" : "Advisor"}
                  </span>
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isDark ? "text-slate-400 hover:text-casey-light-blue" : "text-slate-400 hover:text-casey-blue"} transition-colors`}
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <h3 className={`text-xl font-montserrat font-semibold mb-1 ${isDark ? "text-white" : "text-slate-900"}`}>
                  {member.name}
                </h3>
                <p className={`font-medium text-sm mb-3 ${isDark ? "text-casey-light-blue" : "text-casey-blue"}`}>
                  {member.role}
                </p>
                <p className={`text-sm line-clamp-3 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                  {member.bio}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Investment Section
export const InvestmentSectionV3 = () => {
  const { isDark } = useDarkMode();
  
  return (
    <section id="investment" className="py-32 bg-gradient-to-br from-casey-blue via-[#0a2dd4] to-casey-purple overflow-hidden relative">
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-casey-purple/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="flex justify-center mb-8"
        >
          {/* Horizontal logo for dark gradient background */}
          <img
            src={CASEY_LOGOS.light}
            alt="CASEY Logo"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </motion.div>
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full text-sm font-medium font-inter mb-6 bg-white/10 text-white/80"
          >
            Investment Opportunity
          </motion.span>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-montserrat font-bold text-4xl md:text-5xl lg:text-6xl text-white">
              Join Us on
            </h2>
            <h2 className="font-montserrat font-bold text-4xl md:text-5xl lg:text-6xl text-casey-light-blue">
              This Journey
            </h2>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16">
          {/* Fund Allocation */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className={`rounded-2xl md:rounded-3xl p-6 md:p-10 ${isDark ? "bg-slate-900" : "bg-white border border-slate-200 shadow-lg shadow-slate-200/50"}`}
          >
            <h3 className={`text-2xl md:text-3xl font-montserrat font-bold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
              {investmentAsk.amount} <span className={isDark ? "text-casey-light-blue" : "text-casey-blue"}>{investmentAsk.round}</span>
            </h3>
            <p className={`text-lg mb-2 ${isDark ? "text-white/80" : "text-slate-700"}`}>
              {investmentAsk.valuation}
            </p>
            <p className={`text-sm mb-4 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Raising $1M to build Career Intelligence 2.0.
            </p>
            <div className="flex items-center gap-3 mb-10">
              <p className={`${isDark ? "text-slate-500" : "text-slate-500"}`}>Use of Funds</p>
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-medium">
                {investmentAsk.raised} {investmentAsk.raisedNote}
              </span>
            </div>

            <div className="space-y-8">
              {investmentAsk.allocation.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className={`font-montserrat font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {item.category}
                    </span>
                    <span className={isDark ? "text-slate-400" : "text-slate-600"}>
                      {item.amount} ({item.percentage}%)
                    </span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${item.percentage}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                      className="h-full rounded-full"
                      style={{ background: isDark 
                        ? (index === 0 ? "#7b8cff" : index === 1 ? "#a78bfa" : "#c4b5fd")
                        : (index === 0 ? "#011bc4" : index === 1 ? "#765ccb" : "#7b8cff") 
                      }}
                    />
                  </div>
                  <p className={`text-sm mt-2 ${isDark ? "text-slate-500" : "text-slate-500"}`}>{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Goals & Contact */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-10 border border-white/10">
              <h3 className="text-2xl font-montserrat font-semibold text-white mb-8">
                What We'll Achieve
              </h3>
              {investmentAsk.goals.map((goal, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 py-5 border-b border-white/10 last:border-0"
                >
                  <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="text-3xl font-montserrat font-bold text-white">
                      {goal.metric}
                    </div>
                    <div className="text-white/50">{goal.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-10 border border-white/10">
              <h3 className="text-2xl font-montserrat font-semibold text-white mb-6">Contact Us</h3>
              <div className="space-y-4">
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group"
                >
                  <Mail className="w-5 h-5" />
                  {companyInfo.email}
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                <a
                  href={companyInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group"
                >
                  <Linkedin className="w-5 h-5" />
                  LinkedIn
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                <a
                  href={companyInfo.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group"
                >
                  <Globe className="w-5 h-5" />
                  {companyInfo.website}
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <motion.a
            href={companyInfo.calendly}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05, boxShadow: "0 25px 50px rgba(0,0,0,0.3)" }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-casey-blue px-8 md:px-12 py-4 md:py-5 rounded-full font-semibold text-base md:text-lg inline-flex items-center gap-3 shadow-2xl shadow-black/20 font-montserrat"
          >
            <Calendar className="w-5 h-5" />
            Schedule a Call
          </motion.a>
          <motion.a
            href={`mailto:${companyInfo.email}`}
            whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
            whileTap={{ scale: 0.95 }}
            className="border-2 border-white/50 text-white px-8 md:px-12 py-4 md:py-5 rounded-full font-semibold text-base md:text-lg inline-flex items-center gap-3 transition-colors font-montserrat"
          >
            <Mail className="w-5 h-5" />
            Email Us
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Section
export const FooterSectionV3 = () => {
  const { isDark } = useDarkMode();
  
  return (
    <footer className={`py-12 md:py-16 transition-colors duration-500 ${isDark ? "bg-slate-950" : "bg-slate-900"}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Large Logo - Horizontal */}
        <div className="flex justify-center mb-10 md:mb-12">
          <motion.img
            src={CASEY_LOGOS.light}
            alt="CASEY"
            className="h-12 md:h-16 w-auto object-contain"
            whileHover={{ scale: 1.05 }}
          />
        </div>

        {/* Newsletter Signup Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <h3 className="text-xl md:text-2xl font-montserrat font-semibold text-white mb-3">
            Stay Updated with CASEY
          </h3>
          <p className="text-white/60 mb-6 max-w-md mx-auto">
            Subscribe to our investor newsletter for the latest updates on our journey
          </p>
          <motion.a
            href={companyInfo.newsletter}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(1, 27, 196, 0.4)" }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-casey-blue to-casey-purple text-white px-8 py-4 rounded-full font-semibold text-lg font-montserrat"
          >
            <Newspaper className="w-5 h-5" />
            <span>Subscribe to Newsletter</span>
            <ArrowUpRight className="w-5 h-5" />
          </motion.a>
        </motion.div>
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
          <div className="flex items-center gap-4">
            {/* Logo */}
            <img
              src={CASEY_LOGOS.light}
              alt="CASEY"
              className="h-6 md:h-8 w-auto object-contain"
            />
            <span className="text-white/30">|</span>
            <span className="text-white/50 text-sm">{companyInfo.headquarters}</span>
          </div>

          <div className="flex items-center gap-6">
            <motion.a
              href={companyInfo.newsletter}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, color: "#fff" }}
              className="text-white/50 transition-colors"
              title="Newsletter"
            >
              <Newspaper className="w-5 h-5" />
            </motion.a>
            <motion.a
              href={companyInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, color: "#fff" }}
              className="text-white/50 transition-colors"
            >
              <Linkedin className="w-5 h-5" />
            </motion.a>
            <motion.a
              href={`mailto:${companyInfo.email}`}
              whileHover={{ scale: 1.2, color: "#fff" }}
              className="text-white/50 transition-colors"
            >
              <Mail className="w-5 h-5" />
            </motion.a>
            <motion.a
              href={companyInfo.website}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, color: "#fff" }}
              className="text-white/50 transition-colors"
            >
              <Globe className="w-5 h-5" />
            </motion.a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-white/30 text-sm">
            © {new Date().getFullYear()} CASEY AI, Co. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
